/**
 * Integrytas Group — Mobile UI
 *
 * Builds the "Translations" group for a mobile row's related-children section
 * (§17.B14) — the mobile counterpart of
 * `src/components/common/translations_group.ts`.
 *
 * Both adapters read the same `translationItems()` (§16: the data layer is
 * shared, the presentation is not), so a code's translations carry the same
 * flag, the same language naming and the same ordering on both surfaces.
 *
 * ONE DELIBERATE DIFFERENCE. Desktop puts the language name in a hover `title`;
 * mobile puts it in the visible `sub` line, because a touch device has no hover
 * — a tooltip there is content the reader can never reach. The mobile row
 * therefore reads "🇲🇽 Contador / Mexican Spanish — ES_MX" on two lines, which is
 * also why this file exists instead of the desktop builder being reused.
 */
import { translationItems, type TranslationLike } from '../../lib/translation_items';
import type { MobileChildGroup } from './MobileRelatedChildren';

export type { TranslationLike };

/** Minimal `t` shape — avoids dragging i18next's TFunction generics into callers. */
type Translate = (key: string, defaultValue: string) => string;

/**
 * @param tl      the row's `_tl` records (safe to pass undefined/null)
 * @param t       the caller's `t` from useTranslation()
 * @param locale  the ACTIVE CSM locale
 */
export function buildMobileTranslationsGroup(
  tl: TranslationLike[] | null | undefined,
  t: Translate,
  locale: string,
): MobileChildGroup {
  return {
    key: 'translations',
    title: t('common.translations_lbl', 'Translations'),
    items: translationItems(tl, locale).map(x => ({
      id: x.id,
      label: x.label,
      sub: x.hover,
      icon: x.icon,
    })),
  };
}
